import { reportingPrisma as prisma, PaymentStatus } from "client";

export const getRevenueSummaryData = async ({
  searchParams,
  chartId,
  whiteLabelId,
  organizationId,
}: {
  searchParams: { [key: string]: string };
  chartId: number;
  whiteLabelId?: string;
  organizationId?: string;
}): Promise<{
  "Total Revenue": number;
  "Monthly Revenue": Array<{ date: string; "Total Revenue Per Month": number }>;
  currency: string | null;
}> => {
  try {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const currentMonth = !isNaN(parseInt(searchParams[`toDate${chartId}`]))
      ? new Date(searchParams[`toDate${chartId}`]).getMonth() + 1
      : new Date().getMonth() + 1;

    const currentYear = !isNaN(parseInt(searchParams[`toDate${chartId}`]))
      ? new Date(searchParams[`toDate${chartId}`]).getFullYear()
      : new Date().getFullYear();

    let FiltrationStages: any[] = [];
    let fromDate: Date;
    let toDate: Date;

    // Generate 12 months starting from the previous month
    fromDate = !isNaN(parseInt(searchParams[`fromDate${chartId}`]))
      ? new Date(searchParams[`fromDate${chartId}`])
      : new Date(currentYear, currentMonth - 12, 1); // Default to 12 months ago if fromDate is not provided
    toDate = !isNaN(parseInt(searchParams[`toDate${chartId}`]))
      ? new Date(searchParams[`toDate${chartId}`])
      : // :
        // searchParams[`fromDate${chartId}`]
        // ? new Date(
        //     new Date(searchParams[`toDate${chartId}`).setMonth(
        //      searchParams[`fromDate${chartId}`].getMonth() + 12
        //     )
        //   )
        new Date(currentYear, currentMonth, 1);

    switch (searchParams[`toggle${chartId}`]) {
      case "12 months":
        fromDate = new Date(currentYear, currentMonth - 12, 1);
        toDate = new Date(currentYear, currentMonth, 0);
        break;
      case "30 days":
        fromDate = new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000);
        toDate = new Date();
        break;
      case "7 days":
        fromDate = new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000);
        toDate = new Date();
        break;
      case "24 hours":
        fromDate = new Date(new Date().getTime() - 24 * 60 * 60 * 1000);
        toDate = new Date();
        break;
      default:
        // Default to 12 months
        // fromDate = new Date(currentYear, currentMonth - 12, 1);
        // toDate = new Date(currentYear, currentMonth, 0);
        break;
    }

    // Loop to generate months
    const months = [];

    for (
      let date = new Date(fromDate);
      date <= toDate;
      date.setMonth(date.getMonth() + 1)
    ) {
      months.push({ month: date.getMonth() + 1, year: date.getFullYear() });
    }

    // Construct the fromDate and toDate strings for filtering
    const fromDateStr = fromDate.toISOString();
    const toDateStr = toDate.toISOString();

    const needsEventLookup =
      searchParams[`whiteLabelIds${chartId}`] ||
      whiteLabelId ||
      organizationId ||
      searchParams[`eventIds${chartId}`];

    if (organizationId) {
      // Filter by organization - events have organizationIds array
      FiltrationStages = [
        {
          $lookup: {
            from: "Order",
            localField: "orderId",
            foreignField: "_id",
            as: "order",
          },
        },
        { $unwind: { path: "$order", preserveNullAndEmptyArrays: true } },
        {
          $lookup: {
            from: "Event",
            localField: "order.eventId",
            foreignField: "_id",
            as: "event",
          },
        },
        { $unwind: { path: "$event", preserveNullAndEmptyArrays: true } },
        {
          $match: {
            $expr: {
              $in: [
                { $oid: organizationId },
                {
                  $ifNull: ["$event.organizationIds", []],
                },
              ],
            },
          } as any,
        },
      ];
      if (searchParams[`eventIds${chartId}`]) {
        const eventIds = searchParams[`eventIds${chartId}`]?.split(",");

        FiltrationStages = [
          ...FiltrationStages,
          {
            $match: {
              "event._id": { $in: eventIds.map((id) => ({ $oid: id })) },
            },
          },
        ];
      }
    } else if (searchParams[`whiteLabelIds${chartId}`] || whiteLabelId) {
      const whiteLabelIds = searchParams[`whiteLabelIds${chartId}`]?.split(
        ","
      ) || [whiteLabelId];

      FiltrationStages = [
        {
          $lookup: {
            from: "Order",
            localField: "orderId",
            foreignField: "_id",
            as: "order",
          },
        },
        { $unwind: { path: "$order", preserveNullAndEmptyArrays: true } },
        {
          $lookup: {
            from: "Event",
            localField: "order.eventId",
            foreignField: "_id",
            as: "event",
          },
        },
        { $unwind: { path: "$event", preserveNullAndEmptyArrays: true } },
        {
          $match: {
            "event.whiteLabelId": {
              $in: whiteLabelIds.map((id) => ({ $oid: id })),
            },
          },
        },
      ];
      if (searchParams[`eventIds${chartId}`]) {
        const eventIds = searchParams[`eventIds${chartId}`]?.split(",");

        FiltrationStages = [
          ...FiltrationStages,
          {
            $match: {
              "event._id": { $in: eventIds.map((id) => ({ $oid: id })) },
            },
          },
        ];
      }
    } else if (searchParams[`eventIds${chartId}`]) {
      // If only eventIds filter is provided, we still need to lookup Order and Event
      const eventIds = searchParams[`eventIds${chartId}`]?.split(",");

      FiltrationStages = [
        {
          $lookup: {
            from: "Order",
            localField: "orderId",
            foreignField: "_id",
            as: "order",
          },
        },
        { $unwind: { path: "$order", preserveNullAndEmptyArrays: true } },
        {
          $lookup: {
            from: "Event",
            localField: "order.eventId",
            foreignField: "_id",
            as: "event",
          },
        },
        { $unwind: { path: "$event", preserveNullAndEmptyArrays: true } },
        {
          $match: {
            "event._id": { $in: eventIds.map((id) => ({ $oid: id })) },
          },
        },
      ];
    } else {
      // Even if no filters, lookup Order and Event to get currency
      FiltrationStages = [
        {
          $lookup: {
            from: "Order",
            localField: "orderId",
            foreignField: "_id",
            as: "order",
          },
        },
        { $unwind: { path: "$order", preserveNullAndEmptyArrays: true } },
        {
          $lookup: {
            from: "Event",
            localField: "order.eventId",
            foreignField: "_id",
            as: "event",
          },
        },
        { $unwind: { path: "$event", preserveNullAndEmptyArrays: true } },
      ];
    }

    // Add currency filter if provided
    if (searchParams[`currency${chartId}`]) {
      const selectedCurrency = searchParams[`currency${chartId}`];
      // Ensure we have the event lookup stages
      if (FiltrationStages.length === 0) {
        FiltrationStages = [
          {
            $lookup: {
              from: "Order",
              localField: "orderId",
              foreignField: "_id",
              as: "order",
            },
          },
          { $unwind: { path: "$order", preserveNullAndEmptyArrays: true } },
          {
            $lookup: {
              from: "Event",
              localField: "order.eventId",
              foreignField: "_id",
              as: "event",
            },
          },
          { $unwind: { path: "$event", preserveNullAndEmptyArrays: true } },
        ];
      }
      // Add currency filter
      FiltrationStages.push({
        $match: {
          "event.currency": selectedCurrency,
        },
      });
    }

    // Build aggregation pipeline starting from Payment collection
    // Revenue should be calculated from payments with PAID status
    const stages = [
      { $sort: { _id: -1 } },
      // Filter by payment status - only include PAID payments
      {
        $match: {
          status: PaymentStatus.PAID,
        },
      },
      // Filter by date range - use updatedAt (when payment was completed) or createdAt
      {
        $match: {
          $expr: {
            $and: [
              {
                $gte: [
                  {
                    $ifNull: ["$updatedAt", "$createdAt"],
                  },
                  {
                    $dateFromString: {
                      dateString: new Date(fromDateStr).toISOString(),
                    },
                  },
                ],
              },
              {
                $lte: [
                  {
                    $ifNull: ["$updatedAt", "$createdAt"],
                  },
                  {
                    $dateFromString: {
                      dateString: new Date(toDateStr).toISOString(),
                    },
                  },
                ],
              },
            ],
          } as any,
        },
      },
      ...FiltrationStages,
      // Group by month using payment date (updatedAt when completed, or createdAt)
      {
        $group: {
          _id: {
            $dateToString: {
              format: "%Y-%m",
              date: {
                $ifNull: ["$updatedAt", "$createdAt"],
              },
              timezone: userTimeZone,
            },
          },
          total: {
            $sum: {
              $ifNull: ["$grandTotal", 0],
            },
          },
          // Collect currencies for this month
          currencies: { $addToSet: "$event.currency" },
        },
      },
      {
        $group: {
          _id: null,
          totalRevenue: { $sum: "$total" },
          data: {
            $push: { date: "$_id", totalRevenuePerMonth: "$total" },
          },
          // Flatten all currencies from all months
          allCurrencies: { $push: "$currencies" },
        },
      },
      {
        $project: {
          _id: 0,
          totalRevenue: 1,
          data: 1,
          allCurrencies: 1,
        },
      },
    ];
    const revenueSummary: any = await prisma.payment.aggregateRaw({
      pipeline: stages,
    });
    if (revenueSummary == null || !revenueSummary.length) {
      // Handle the case where revenueSummary is null or undefined
      return {
        "Total Revenue": 0,
        "Monthly Revenue": [],
        currency: null,
      };
    }

    // Get the most common currency from the currencies array
    // allCurrencies is an array of arrays, so we need to flatten it
    const allCurrencies = revenueSummary[0].allCurrencies || [];
    const flattenedCurrencies: (string | null)[] = [];
    allCurrencies.forEach((currencyArray: (string | null)[]) => {
      if (Array.isArray(currencyArray)) {
        flattenedCurrencies.push(...currencyArray);
      }
    });

    const currencyCounts = new Map<string, number>();
    flattenedCurrencies.forEach((currency: string | null) => {
      if (currency) {
        currencyCounts.set(currency, (currencyCounts.get(currency) || 0) + 1);
      }
    });

    // Find the most common currency, default to SAR if none found
    let mostCommonCurrency: string | null = "SAR";
    let maxCount = 0;
    currencyCounts.forEach((count, currency) => {
      if (count > maxCount) {
        maxCount = count;
        mostCommonCurrency = currency;
      }
    });

    // Fill in missing months with zero revenue
    const result = months.map(({ month, year }) => {
      // Format month with zero-padding to match aggregation format (YYYY-MM)
      const monthStr = month.toString().padStart(2, "0");
      const dateKey = `${year}-${monthStr}`;
      return {
        date: `${getMonthName(month)} ${year.toString().slice(2)}`,
        "Total Revenue Per Month":
          revenueSummary[0].data.find((item: any) => item.date === dateKey)
            ?.totalRevenuePerMonth || 0,
      };
    });

    return {
      "Total Revenue": revenueSummary[0].totalRevenue,
      "Monthly Revenue": result,
      currency: mostCommonCurrency,
    };
  } catch (e: any) {
    console.error(e);
    return {
      "Total Revenue": 0,
      "Monthly Revenue": [],
      currency: null,
    };
  }
};

// Function to get month name from month number
const getMonthName = (month: number) => {
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return monthNames[month - 1];
};
